import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";

// Define the context type
interface ThemeContextType {
  toggleTheme: () => void;
  isDarkMode: boolean;
}

// Define a context
const ThemeContext = createContext<ThemeContextType>({
  toggleTheme: () => {},
  isDarkMode: false,
});

// Theme Provider Component
export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check localStorage for the initial theme or default to light mode
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem("isDarkMode");
    return savedTheme ? JSON.parse(savedTheme) : false; // Default to light mode if not set
  });

  // Toggle the theme and persist the selection in localStorage
  const toggleTheme = () => {
    setIsDarkMode((prevMode: boolean) => {
      const newMode = !prevMode;
      localStorage.setItem("isDarkMode", JSON.stringify(newMode)); // Save to localStorage
      return newMode;
    });
  };

  // Memoize the theme object
const theme = useMemo(
  () =>
    createTheme({
      palette: {
        mode: isDarkMode ? "dark" : "light",
        primary: {
          main: isDarkMode ? '#90caf9' : '#1976d2',
        },
        secondary: {
          main: isDarkMode ? '#f48fb1' : '#d81b60',
        },
        background: {
          default: isDarkMode ? '#121212' : '#f5f5f5',
          paper: isDarkMode ? '#1e1e1e' : '#ffffff',
        },
      },
      typography: {
        fontFamily: 'Inter, sans-serif',
      },
      shape: {
        borderRadius: 8,
      },
    }),
  [isDarkMode]
);

  // Ensure the theme is applied on initial load (useful for SSR or hydration issues)
  useEffect(() => {
    document.body.style.backgroundColor = isDarkMode ? "#121212" : "#ffffff";
  }, [isDarkMode]);

  return (
    <ThemeContext.Provider value={{ toggleTheme, isDarkMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline /> {/* Ensures proper dark/light mode styles */}
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom Hook to use Theme Context
export const useThemeContext = () => useContext(ThemeContext);
